// src/components/layout/ChatColumn/CollapsedCodeCard.jsx
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Code, ArrowUpRight, Copy, Check, Trash2, Loader2 } from 'lucide-react';
import { usePanelManager, PanelTypes, CONTENT_TYPES } from '@/managers/PanelManager';

/* ========================
 * calculateLineCount - Computes the number of lines in a string.
 * An empty string is correctly treated as having 1 line.
 * ========================*/
const calculateLineCount = (str) => {
    if (!str) return 1;
    // ARCHITECTURAL FIX: A regex match is significantly faster for counting occurrences
    // in large strings compared to a manual JavaScript loop, reducing blocking on the main thread.
    return (str.match(/\n/g) || []).length + 1;
};

const CollapsedCodeCard = ({
    code,
    language,
    messageId,
    blockId,
    isStreaming = false,
    minLines = 3,
    maxLines = 14,
    onRedact,
}) => {
    const {
        showInPortal,
        togglePanelWithMemory,
        isPanelOpen,
        getPortalContent
    } = usePanelManager();

    const [isHovered, setIsHovered] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);
    const [isRedacting, setIsRedacting] = useState(false);
    const [isConfirmingRedact, setIsConfirmingRedact] = useState(false);
    
    // Throttled line count to prevent flickering during streaming
    const [lineCount, setLineCount] = useState(() => calculateLineCount(code));
    
    const clickTimeoutRef = useRef(null);
    const confirmTimeoutRef = useRef(null);
    const clickCountRef = useRef(0);
    const codeRef = useRef(code);
    
    useEffect(() => {
        codeRef.current = code;
    }, [code]);
    
    /* ========================
     * Stabilized Line Count Effect (Architectural Fix)
     * This effect now only depends on `isStreaming`. During a stream, it updates the line
     * count on a relaxed interval (750ms) to prevent rapid, jarring re-renders. A final,
     * accurate count is performed when streaming stops. This eliminates UI flicker.
     * ========================*/
    useEffect(() => {
        if (isStreaming) {
            // While streaming, update the line count periodically using a ref to get the latest code.
            const intervalId = setInterval(() => {
                setLineCount(calculateLineCount(codeRef.current));
            }, 750); // Update every 750ms, much less disruptive.

            // Cleanup function to clear the interval when isStreaming changes or the component unmounts.
            return () => clearInterval(intervalId);
        } else {
            // When streaming is finished, or was never active, do one final, accurate count.
            setLineCount(calculateLineCount(codeRef.current));
        }
    }, [isStreaming]);

    const isMarkdown = useMemo(() => language?.toLowerCase() === 'markdown', [language]);
    const portalContentTypeForThisBlock = isMarkdown ? CONTENT_TYPES.DOCUMENT : CONTENT_TYPES.CODE;

    const portalContent = getPortalContent();
    const isThisBlockInPortal = useMemo(() =>
        isPanelOpen(PanelTypes.PORTAL) &&
        portalContent?.type === portalContentTypeForThisBlock &&
        portalContent?.metadata?.messageId === messageId &&
        portalContent?.metadata?.blockId === blockId,
        [isPanelOpen, portalContent, portalContentTypeForThisBlock, messageId, blockId]
    );

    const handleCopy = useCallback((e) => {
        e.stopPropagation();
        if (copySuccess) return;
        
        navigator.clipboard.writeText(codeRef.current)
            .then(() => {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 1500);
            })
            .catch(err => console.error('Failed to copy code:', err));
    }, [copySuccess]);

    const handlePortalToggle = useCallback(async (e) => {
        e.stopPropagation();
        if (isThisBlockInPortal) {
            togglePanelWithMemory(PanelTypes.PORTAL);
        } else {
            await showInPortal({
                type: portalContentTypeForThisBlock,
                content: codeRef.current,
                metadata: { language, messageId, blockId, contentType: portalContentTypeForThisBlock, title: language }
            });
        }
    }, [showInPortal, togglePanelWithMemory, language, messageId, blockId, isThisBlockInPortal, portalContentTypeForThisBlock]);

    const handleRedactionClick = useCallback(async (e) => {
        e.stopPropagation();
        if (!onRedact || isRedacting || isStreaming) return;

        if (isConfirmingRedact) {
            if (confirmTimeoutRef.current) clearTimeout(confirmTimeoutRef.current);
            
            setIsRedacting(true);
            try {
                await onRedact(messageId, {
                    blockId: blockId,
                    language: language,
                    codeToRedact: codeRef.current,
                });
            } catch (err) {
                console.error("Failed to redact code block", err);
            } finally {
                // ARCHITECTURAL FIX: Always reset loading and confirmation state
                // This fixes the bug where the button would get stuck.
                setIsRedacting(false);
                setIsConfirmingRedact(false);
            }
        } else {
            setIsConfirmingRedact(true);
            confirmTimeoutRef.current = setTimeout(() => {
                setIsConfirmingRedact(false);
            }, 4000);
        }
    }, [onRedact, isRedacting, isStreaming, isConfirmingRedact, messageId, blockId, language]);

    const handleClick = useCallback((e) => {
        if (e.target.closest('button')) return;

        clickCountRef.current += 1;
        if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
        
        if (clickCountRef.current === 1) {
            clickTimeoutRef.current = setTimeout(() => {
                handlePortalToggle(e);
                clickCountRef.current = 0;
            }, 250);
        } else if (clickCountRef.current === 2) {
            clearTimeout(clickTimeoutRef.current);
            handleCopy(e);
            clickCountRef.current = 0;
        }
    }, [handlePortalToggle, handleCopy]);

    const handleMouseLeave = useCallback(() => {
        setIsHovered(false);
        if (isConfirmingRedact) {
            clearTimeout(confirmTimeoutRef.current);
            setIsConfirmingRedact(false);
        }
    }, [isConfirmingRedact]);

    useEffect(() => () => {
        if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
        if (confirmTimeoutRef.current) clearTimeout(confirmTimeoutRef.current);
    }, []);

    const headerTextSizeClass = 'text-xs';

    const preStyle = useMemo(() => {
        const fontSize = 11;
        const lineHeight = 2;
        const verticalPadding = 16;
        return {
            fontSize: `${fontSize}px`,
            lineHeight: `${lineHeight}`,
            padding: '12px 14px',
            minHeight: `${minLines * fontSize * lineHeight + verticalPadding}px`,
            maxHeight: `${maxLines * fontSize * lineHeight + verticalPadding}px`,
            overflowY: 'auto',
        };
    }, [minLines, maxLines]);

    return (
        <div
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={handleMouseLeave}
            title="Double-click to copy."
            style={{
                background: 'linear-gradient(135deg, var(--code-block-bg-gradient-start), var(--code-block-bg-gradient-end))',
                color: 'var(--code-block-text-color)'
            }}
            className={`
                collapsed-code-card-wrapper w-full group flex flex-col items-start
                mb-4 mb-6 last:mb-0 rounded-lg shadow-sm border cursor-pointer relative transition-colors
                ${isThisBlockInPortal ? 'border-primary shadow-[0_0_15px_rgba(var(--primary-rgb),0.15)]' : 'border-border-subtle hover:border-surface-dark'}
            `}
            role="button" tabIndex={0}
            aria-label={`Code block: ${language}, ${lineCount} lines. Click to view, double click to copy.`}
        >
            <div className={`absolute inset-0 rounded-lg bg-primary z-20 pointer-events-none ${copySuccess ? 'animate-flash' : 'opacity-0'}`} />
            <div className="relative z-10 flex flex-col w-full">
                <div 
                    className="flex items-center gap-2 w-full text-on-surface-dim px-4 py-2 rounded-t-lg"
                    style={{ backgroundColor: 'var(--code-block-header-bg)' }}
                >
                    <div className={`p-1.5 rounded-md transition-colors ${isThisBlockInPortal ? 'bg-[rgba(var(--primary-rgb),0.25)] text-primary' : isHovered ? 'bg-[rgba(var(--primary-rgb),0.15)] text-primary-light' : 'bg-[rgba(var(--surface-dark-rgb),0.3)]'} ${isStreaming ? 'animate-pulseGlow' : ''}`}>
                        <Code className="w-4 h-4" />
                    </div>
                    <span className={`font-semibold tracking-wide ${headerTextSizeClass} ${isThisBlockInPortal ? 'text-primary' : ''}`}>{language}</span>
                    <span className={`${headerTextSizeClass} flex items-center`}>{isStreaming ? `Writing Code... ${lineCount} line${lineCount !== 1 ? 's' : ''}` : `${lineCount} line${lineCount !== 1 ? 's' : ''}`}</span>
                    <div className="flex-1"></div>
                    <div className={`flex items-center gap-2 transition-opacity ${isHovered || isThisBlockInPortal ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                        {onRedact && !isStreaming && (
                            <button
                                onClick={handleRedactionClick}
                                className={`flex items-center gap-1.5 p-1 rounded-md text-xs transition-colors ${isConfirmingRedact ? 'bg-red-500/20 text-red-400' : 'hover:bg-red-500/20 hover:text-red-500'}`}
                                title={isConfirmingRedact ? "Click again to confirm" : "Permanently remove this code block"}
                                aria-label={isRedacting ? "Removing..." : isConfirmingRedact ? "Confirm removal" : "Remove this code block"}
                                disabled={isRedacting}
                            >
                                {isRedacting ? (
                                    <Loader2 className="w-3.5 h-3.5 text-red-500 animate-spin" />
                                ) : (
                                    <Trash2 className={`w-3.5 h-3.5 transition-colors ${isConfirmingRedact ? 'text-red-400' : 'text-on-surface-dim group-hover:text-red-500'}`} />
                                )}
                                {isConfirmingRedact && !isRedacting && <span>Confirm</span>}
                            </button>
                        )}
                        <button
                            onClick={handleCopy}
                            className="p-1 rounded-md hover:bg-[rgba(var(--on-surface-rgb),0.1)]"
                            aria-label={copySuccess ? "Copied!" : "Copy code"}
                            disabled={copySuccess}
                        >
                            {copySuccess ? <Check className="w-3.5 h-3.5 text-primary" /> : <Copy className="w-3.5 h-3.5 text-on-surface-dim" />}
                        </button>
                        <button
                            onClick={handlePortalToggle}
                            className="p-1 rounded-md hover:bg-[rgba(var(--on-surface-rgb),0.1)]"
                            aria-label={isThisBlockInPortal ? "Close in Portal" : "View in Portal"}
                        >
                            <ArrowUpRight className={`w-3.5 h-3.5 transition-transform ${isThisBlockInPortal ? 'text-primary rotate-45' : 'text-on-surface-dim'}`} />
                        </button>
                    </div>
                </div>
                <pre className="w-full font-mono collapsed-code-card-pre" style={preStyle}>
                    <code className="block w-full">{code}</code>
                </pre>
            </div>
        </div>
    );
};

CollapsedCodeCard.propTypes = {
    code: PropTypes.string.isRequired,
    language: PropTypes.string.isRequired,
    messageId: PropTypes.string.isRequired,
    blockId: PropTypes.string.isRequired,
    isStreaming: PropTypes.bool,
    minLines: PropTypes.number,
    maxLines: PropTypes.number,
    onRedact: PropTypes.func,
};

CollapsedCodeCard.displayName = 'CollapsedCodeCard';

export default React.memo(CollapsedCodeCard);